import _isMatch from 'lodash/isMatch'
import _groupBy from 'lodash/groupBy'
import { OptionData } from '../nextgen/options'
import {
  Criterion,
  Participant,
  ParticipationSession,
  Rating,
  RatingContextType,
  RatingSubjectType,
  ValidRating,
} from '../types'
import { vectorMean } from '../valuemetrics/util'

type RatingIdKeys = 'participationSessionId' | 'participantId' | 'contextId' | 'subjectId' | 'subjectType'
export type RatingIdProps = Pick<Rating, RatingIdKeys>

/**
 * given an object containing the properties that uniquely identify a rating,
 * returns a composite identifier that can be used as an index or reference key.
 */
export function identifyRating(rating: RatingIdProps): string {
  const { participationSessionId, participantId, contextId, subjectId, subjectType } = rating
  return [participationSessionId, participantId, contextId, subjectId, subjectType].join(':')
}

/**
 * given an object or an array of objects containing one or more of the
 * properties that are used in a composite rating id, returns a predicate
 * to match rating ids where all provided property values are equal in one
 * of the match objects.
 */
export function ratingIdMatchesWith<T extends Partial<RatingIdProps>>(match: T | T[]) {
  return (ratingId: string) => {
    const [participationSessionId, participantId, contextId, subjectId, subjectType] = ratingId.split(':')
    const ratingProps = {
      participationSessionId,
      participantId,
      contextId,
      subjectId,
      subjectType,
    }

    if (Array.isArray(match)) {
      return match.some(m => _isMatch(ratingProps, m))
    }
    return _isMatch(ratingProps, match)
  }
}

type RatingWarningCode = 'ReferenceUnknown' | 'InvalidSubject'
type CheckedRatings = {
  ratings: Rating[]
  warnings: { code: RatingWarningCode; message: string }[]
}

function getRatingValidator(
  participationSession: ParticipationSession,
  participants: Record<string, Participant>,
  criteria: Record<string, Criterion>,
  options: Record<string, OptionData>,
  warnings: CheckedRatings['warnings']
): (r: Rating) => boolean {
  switch (participationSession.type) {
    case 'CriteriaPrioritization':
      return r => {
        const newWarnings: CheckedRatings['warnings'] = []
        if (!participants[r.participantId]) {
          newWarnings.push({ code: 'ReferenceUnknown', message: `participant ${r.participantId} not found` })
        }
        if (!criteria[r.contextId]) {
          newWarnings.push({ code: 'ReferenceUnknown', message: `context criterion ${r.contextId} not found` })
        }
        if (!criteria[r.subjectId]) {
          newWarnings.push({ code: 'ReferenceUnknown', message: `subject criterion ${r.subjectId} not found` })
        }
        if (r.subjectType !== 'Criterion') {
          newWarnings.push({
            code: 'InvalidSubject',
            message: `invalid subject for criteria prioritization: ${r.subjectType}`,
          })
        }
        warnings.push(...newWarnings)
        return newWarnings.length === 0
      }
    case 'OptionRating': {
      return r => {
        const newWarnings: CheckedRatings['warnings'] = []
        if (!participants[r.participantId]) {
          newWarnings.push({ code: 'ReferenceUnknown', message: `participant ${r.participantId} not found` })
        }
        if (!criteria[r.contextId]) {
          newWarnings.push({ code: 'ReferenceUnknown', message: `context criterion ${r.contextId} not found` })
        }
        if (!options[r.subjectId]) {
          newWarnings.push({ code: 'ReferenceUnknown', message: `option ${r.subjectId} not found` })
        }
        if (r.subjectType !== 'Option') {
          newWarnings.push({
            code: 'InvalidSubject',
            message: `invalid subject for option rating: ${r.subjectType}`,
          })
        }
        warnings.push(...newWarnings)
        return newWarnings.length === 0
      }
    }
    default:
      throw new Error('unrecognized participation session type:' + participationSession.type)
  }
}

/**
 * Given a list of ratings, filters out any rating that includes an invalid reference (i.e. to
 * participant, criteria, or option).  It also returns a list of warnings about any invalid ratings
 * that were provided.
 */
export function validateRatings(
  ratings: Rating[],
  participationSession: ParticipationSession,
  participants: Record<string, Participant>,
  criteria: Record<string, Criterion>,
  options: Record<string, OptionData>
): CheckedRatings {
  const warnings: CheckedRatings['warnings'] = []
  const ratingValidator = getRatingValidator(participationSession, participants, criteria, options, warnings)
  return {
    ratings: ratings.filter(ratingValidator),
    warnings,
  }
}

export function aggregateParticipantRatings(
  ratings: Rating[],
  xAxisTransform: 'None' | 'Space Evenly' = 'Space Evenly'
) {
  const validRatings = ratings.filter((r): r is ValidRating => !r.abstain && r.ratingVector !== null)
  const keyedRatings = _groupBy(
    validRatings,
    r => `${r.participationSessionId},${r.contextType}=${r.contextId},${r.subjectType}=${r.subjectId}`
  )
  const aggregated = Object.entries(keyedRatings).map(([k, ratings]) => {
    const [participationSessionId, contextType, contextId, subjectType, subjectId] = k.split(/[,=]/g) as [
      string,
      RatingContextType,
      string,
      RatingSubjectType,
      string
    ]
    const aggregate: ValidRating = {
      participationSessionId,
      participantId: '*',
      contextType,
      contextId,
      subjectType,
      subjectId,
      abstain: false,
      ratingVector: vectorMean(ratings.map(r => r.ratingVector)),
      updated: {
        timestamp: Date.now(),
        location: '{}',
      },
    }
    return aggregate
  })
  switch (xAxisTransform) {
    case 'Space Evenly':
      // instead of using the average, we're evenly spacing the ratings out on the
      // x-axis, after sorting by rating
      Object.values(_groupBy(aggregated, r => `${r.contextId},${r.subjectType}`)).forEach(ratings => {
        ratings.forEach((r, idx, l) => {
          r.ratingVector[1] = (idx / (l.length - 1)) * 8 + 1
        })
      })
      break
    case 'None':
      break
    default:
      console.warn(`unrecognized xAxisTransform value`)
  }
  return aggregated
}
